import {
  CreateTravellerPayload,
  useCreateTraveller,
} from 'lib/react-query/Queries/useCreateTraveller';
import { SubmitTravellerOptions, TTravellerSubmit } from 'lib/types/traveller';
import { useReducer, useRef, useState } from 'react';
import { formUserInit, userValidationInit } from 'lib/reducers/traveller/init';
import { userReducer } from 'lib/reducers/traveller/user-reducer';
import { userValidationReducer } from 'lib/reducers/traveller/validation-reducer';
import { useTravellerValidation } from 'lib/hooks/useTravellerValidation';
import { useTenant } from 'lib/hooks/useTenant';

const useTraveller = (): TTravellerSubmit => {
  const {
    isLoading,
    isError,
    data,
    error,
    mutate: submitCreateTraveller,
  } = useCreateTraveller();
  const { isDA } = useTenant();
  const isSubmitting = useRef(false);
  const [state, dispatch] = useReducer(userReducer, formUserInit);
  const [isGotToGo, setIsGotToGo] = useState<boolean>(false);

  const [validationState, validationDispatch] = useReducer(
    userValidationReducer,
    userValidationInit
  );

  const { checkFieldsValidation } = useTravellerValidation({
    state,
    validationDispatch,
  });

  function submit(
    submitData: CreateTravellerPayload,
    options?: SubmitTravellerOptions
  ) {
    if (submitData === null) {
      return;
    }

    if (!isDA) {
      submitCreateTraveller(submitData);
      return;
    }

    // DealsAway waits on Traveller until setup succeeds, so ignore repeat
    // clicks while a call is in flight.
    if (isSubmitting.current) return;
    isSubmitting.current = true;

    // DealsAway captures the lead before Rooms: this setup call never sends
    // rooms. Setup keeps any rooms already saved on the session.
    submitCreateTraveller(
      { ...submitData, selectedRooms: [] },
      {
        onSuccess: () => options?.onSuccess?.(),
        onSettled: () => {
          isSubmitting.current = false;
        },
      }
    );
  }

  return {
    isCreateTravellerLoading: isLoading,
    createTravellerError: error,
    createTravellerDispatch: dispatch,
    createTravellerIsGotToGo: isGotToGo,
    setCreateTravellerIsGotToGo: setIsGotToGo,
    createTravellerData: data,
    submitCreateTraveller: submit,
    isCreateTravellerError: isError,
    travellerState: state,
    validateTraveller: checkFieldsValidation,
    travellerValidationState: validationState,
    travellerValidationDispatch: validationDispatch,
  };
};

export { useTraveller };
